/*
 * Naturlabs Black Friday page (sections/nl-bf-landing.liquid). Vanilla, no dependencies, loaded with defer.
 * Uses the theme API from nl-app.js: NL.cart.add / get, NL.formatMoney, NL.toast, events cart:updated.
 *
 * Parts (each optional, each guarded):
 *   tiles      "Přínosy" toggle on touch (the panel opens on hover / focus on fine pointers in CSS)
 *   ladder     ritual tier rows (aria-pressed) with a live detail line
 *   tabs       gift ideas (ARIA tabs, arrow keys, Home / End); without JS all panels stay visible
 *   bundles    add several variants at once through NL.cart.add (bundles when live, gift "add all")
 *   meter      free shipping progress + number of different products from the real cart
 *   countdown  days / hours / minutes to the start (only when enabled; hidden after the start)
 *   video      hero loop video on desktop only, never with reduced motion or Save-Data
 *   parallax   hero photo drift on desktop with fine pointer
 *   sticky     mobile CTA bar after the hero, hidden while the signup is in view
 */
(() => {
  'use strict';

  const NL = (window.NL = window.NL || {});
  const reduced = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches || document.documentElement.classList.contains('nl-motion-off');
  const saveData = () => Boolean(navigator.connection && navigator.connection.saveData);

  const money = (cents) => typeof NL.formatMoney === 'function'
    ? NL.formatMoney(cents, { trimZeros: true }) : String(Number(cents || 0) / 100);

  /* Texts outside Czech (naturlabs.eu, 17. 10. 2026): data-bf-i18n on the root, Czech defaults below. */
  const i18n = (root) => {
    try {
      return JSON.parse(root.dataset.bfI18n || '{}') || {};
    } catch (e) {
      return {};
    }
  };
  const fmt = (template, values) =>
    String(template).replace(/\{(\w+)\}/g, (match, key) => (key in values ? String(values[key]) : match));

  const parseTiers = (raw) =>
    String(raw || '')
      .split(',')
      .map((pair) => pair.split(':').map((v) => Number(String(v).trim())))
      .filter(([n, pct]) => n > 0 && pct > 0)
      .sort((a, b) => a[0] - b[0]);

  /* ---------------- Tiles: benefits on touch ---------------- */
  const initTiles = (root) => {
    const closeAll = (except) => {
      root.querySelectorAll('[data-bf-tile].is-open').forEach((tile) => {
        if (tile === except) return;
        tile.classList.remove('is-open');
        const btn = tile.querySelector('[data-bf-benefits]');
        if (btn) btn.setAttribute('aria-expanded', 'false');
      });
    };
    root.addEventListener('click', (event) => {
      const btn = event.target.closest('[data-bf-benefits]');
      if (btn) {
        const tile = btn.closest('[data-bf-tile]');
        const open = !tile.classList.contains('is-open');
        closeAll(tile);
        tile.classList.toggle('is-open', open);
        btn.setAttribute('aria-expanded', String(open));
        return;
      }
      if (!event.target.closest('[data-bf-tile]')) closeAll(null);
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeAll(null);
    });
  };

  /* ---------------- Ladder ---------------- */
  const initLadder = (root) => {
    root.querySelectorAll('[data-bf-ladder]').forEach((ladder) => {
      const rows = Array.from(ladder.querySelectorAll('[data-bf-step]'));
      const detail = ladder.querySelector('[data-bf-ladder-detail]');
      const select = (row) => {
        rows.forEach((r) => {
          const on = r === row;
          r.setAttribute('aria-pressed', String(on));
          r.classList.toggle('is-active', on);
        });
        if (detail) detail.textContent = row.dataset.detail || '';
      };
      rows.forEach((row) => row.addEventListener('click', () => select(row)));
      const active = rows.find((r) => r.getAttribute('aria-pressed') === 'true') || rows[0];
      if (active && detail) detail.textContent = active.dataset.detail || '';
    });
  };

  /* ---------------- Gift tabs ---------------- */
  const initTabs = (root) => {
    root.querySelectorAll('[data-bf-tabs]').forEach((list) => {
      const tabs = Array.from(list.querySelectorAll('[data-bf-tab]'));
      if (!tabs.length) return;
      const panels = tabs.map((tab) => document.getElementById(tab.getAttribute('aria-controls')));
      const show = (index, focus) => {
        tabs.forEach((tab, i) => {
          const on = i === index;
          tab.setAttribute('aria-selected', String(on));
          tab.tabIndex = on ? 0 : -1;
          const panel = panels[i];
          if (!panel) return;
          if (on) {
            const wasHidden = panel.hidden;
            panel.hidden = false;
            if (wasHidden) {
              panel.classList.remove('is-entering');
              void panel.offsetWidth;
              panel.classList.add('is-entering');
            }
          } else {
            panel.hidden = true;
          }
        });
        if (focus) {
          tabs[index].focus();
          tabs[index].scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: reduced() ? 'auto' : 'smooth' });
        }
      };
      list.hidden = false;
      const start = Math.max(0, tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true'));
      show(start, false);
      list.addEventListener('click', (event) => {
        const tab = event.target.closest('[data-bf-tab]');
        if (tab) show(tabs.indexOf(tab), false);
      });
      list.addEventListener('keydown', (event) => {
        const current = tabs.indexOf(document.activeElement);
        if (current < 0) return;
        let next = null;
        if (event.key === 'ArrowRight') next = (current + 1) % tabs.length;
        if (event.key === 'ArrowLeft') next = (current - 1 + tabs.length) % tabs.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = tabs.length - 1;
        if (next === null) return;
        event.preventDefault();
        show(next, true);
      });
    });
  };

  /* ---------------- Multi add (bundles, gift "add all") ---------------- */
  const initMultiAdd = (root) => {
    const T = i18n(root);
    root.addEventListener('click', async (event) => {
      const btn = event.target.closest('[data-bf-bundle-add]');
      if (!btn || btn.getAttribute('aria-busy') === 'true') return;
      let items = [];
      try {
        items = JSON.parse(btn.dataset.items || '[]');
      } catch (e) {
        items = [];
      }
      const errorBox = btn.parentElement && btn.parentElement.querySelector('[data-bf-bundle-error]');
      if (errorBox) {
        errorBox.hidden = true;
        errorBox.textContent = '';
      }
      if (!items.length || !NL.cart || typeof NL.cart.add !== 'function') return;
      btn.setAttribute('aria-busy', 'true');
      const label = btn.querySelector('[data-add-label]');
      const original = label ? label.dataset.original || label.textContent : '';
      if (label) label.dataset.original = original;
      try {
        await NL.cart.add(items, { source: 'black-friday', opener: btn });
        btn.classList.add('is-added');
        if (label) label.textContent = T.added || 'Přidáno do košíku';
        window.setTimeout(() => {
          btn.classList.remove('is-added');
          if (label) label.textContent = original;
        }, 2200);
        document.dispatchEvent(
          new CustomEvent('nl:bf:add', { detail: { title: btn.dataset.title || '', items }, bubbles: true })
        );
      } catch (error) {
        const message =
          (error && error.message) || (NL.strings && NL.strings.cartError) || T.addError || 'Přidání do košíku se nepovedlo.';
        if (errorBox) {
          errorBox.textContent = message;
          errorBox.hidden = false;
        } else if (typeof NL.toast === 'function') {
          NL.toast(message, { variant: 'error' });
        }
      } finally {
        btn.removeAttribute('aria-busy');
      }
    });
  };

  /* ---------------- Cart meter ---------------- */
  const initMeter = (root) => {
    const meter = root.querySelector('[data-bf-meter]');
    if (!meter) return;
    const threshold = Number(root.dataset.bfThreshold) || 149900;
    const live = root.dataset.bfLive === 'true';
    const showValues = root.dataset.bfShowValues === 'true';
    const tiers = parseTiers(root.dataset.bfTiers);
    const ship = meter.querySelector('[data-bf-meter-ship]');
    const track = meter.querySelector('[data-bf-meter-track]');
    const fill = meter.querySelector('[data-bf-meter-fill]');
    const ritual = meter.querySelector('[data-bf-meter-ritual]');
    const T = i18n(root);
    let lastTotal = null;

    const productsText = (n) => {
      if (n === 0) return T.empty || 'Košík je zatím prázdný.';
      if (n === 1) return T.one || 'V košíku máte 1 produkt.';
      if (n < 5) return T.few ? fmt(T.few, { n }) : `V košíku máte ${n} různé produkty.`;
      return T.many ? fmt(T.many, { n }) : `V košíku máte ${n} různých produktů.`;
    };

    const render = (cart) => {
      if (!cart || !Array.isArray(cart.items)) return;
      const total = Number(cart.total_price) || 0;
      const left = Math.max(0, threshold - total);
      const pct = Math.min(100, Math.round((total / threshold) * 100));
      if (ship) {
        const strong = document.createElement('strong');
        if (left > 0) {
          strong.textContent = money(left).replace(/ /g, '\u00a0');
          if (T.shipBefore || T.shipAfter) ship.replaceChildren(T.shipBefore || '', strong, T.shipAfter || '');
          else ship.replaceChildren('Do dopravy zdarma vám zbývá ', strong, '.');
        } else {
          strong.textContent = T.shipFree || 'Dopravu máte zdarma.';
          ship.replaceChildren(strong);
        }
      }
      if (fill) fill.style.setProperty('--p', `${pct}%`);
      if (track) track.setAttribute('aria-valuenow', String(pct));
      const distinct = new Set(cart.items.map((item) => item.product_id)).size;
      if (ritual) {
        let text = productsText(distinct);
        if (live && showValues && tiers.length) {
          const reached = tiers.filter(([n]) => distinct >= n).pop();
          const nextTier = tiers.find(([n]) => distinct < n);
          if (reached) {
            text += T.reached ? fmt(T.reached, { pct: reached[1] }) : ` Rituál je výhodnější o ${reached[1]} %.`;
          } else if (nextTier && distinct > 0) {
            const missing = nextTier[0] - distinct;
            const next = missing === 1 ? T.nextOne : T.nextMany;
            text += next
              ? fmt(next, { n: missing, pct: nextTier[1] })
              : ` Ještě ${missing} ${missing === 1 ? 'další produkt' : 'další produkty'} a rituál bude výhodnější o ${nextTier[1]} %.`;
          }
        }
        ritual.textContent = text;
      }
      if (lastTotal !== null && total !== lastTotal && !reduced()) {
        meter.classList.add('is-bump');
        window.setTimeout(() => meter.classList.remove('is-bump'), 700);
      }
      lastTotal = total;
    };

    document.addEventListener('cart:updated', (event) => render(event.detail && event.detail.cart));
    const load = () => {
      if (NL.cart && typeof NL.cart.get === 'function') {
        NL.cart.get().then(render).catch(() => {});
      } else {
        fetch('/cart.js', { credentials: 'same-origin' })
          .then((r) => (r.ok ? r.json() : null))
          .then(render)
          .catch(() => {});
      }
    };
    if ('requestIdleCallback' in window) window.requestIdleCallback(load, { timeout: 2500 });
    else window.setTimeout(load, 800);
  };

  /* ---------------- Countdown ---------------- */
  const initCountdown = (root) => {
    root.querySelectorAll('[data-bf-countdown]').forEach((box) => {
      const target = Date.parse(box.dataset.target || '');
      if (!target || Number.isNaN(target)) return;
      const cells = {
        d: box.querySelector('[data-unit="d"]'),
        h: box.querySelector('[data-unit="h"]'),
        m: box.querySelector('[data-unit="m"]'),
      };
      const pad = (n) => String(n).padStart(2, '0');
      let timer = null;
      const tick = () => {
        const diff = target - Date.now();
        if (diff <= 0) {
          box.hidden = true;
          if (timer) window.clearInterval(timer);
          return;
        }
        const minutes = Math.floor(diff / 60000);
        const d = Math.floor(minutes / 1440);
        const h = Math.floor((minutes % 1440) / 60);
        const m = minutes % 60;
        if (cells.d) cells.d.textContent = pad(d);
        if (cells.h) cells.h.textContent = pad(h);
        if (cells.m) cells.m.textContent = pad(m);
        box.hidden = false;
      };
      tick();
      timer = window.setInterval(tick, 30000);
    });
  };

  /* ---------------- Hero video ---------------- */
  const initVideo = (root) => {
    const video = root.querySelector('[data-bf-video]');
    if (!video) return;
    const desktop = window.matchMedia('(min-width: 768px)');
    if (!desktop.matches || reduced() || saveData()) return;
    const start = () => {
      if (!video.src) video.src = video.dataset.src;
      video.addEventListener(
        'playing',
        () => {
          video.classList.add('is-playing');
          const hero = video.closest('[data-bf-hero]');
          if (hero) hero.classList.add('is-video-playing');
        },
        { once: true }
      );
      const p = video.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    };
    if (!('IntersectionObserver' in window)) {
      start();
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) start();
          else if (!video.paused) video.pause();
        });
      },
      { threshold: 0.2 }
    );
    io.observe(video);
  };

  /* ---------------- Hero parallax ---------------- */
  const initParallax = (root) => {
    const media = root.querySelector('[data-bf-parallax]');
    const hero = root.querySelector('[data-bf-hero]');
    if (!media || !hero) return;
    const ok = window.matchMedia('(min-width: 768px) and (hover: hover) and (pointer: fine)');
    let ticking = false;
    const update = () => {
      ticking = false;
      if (!ok.matches || reduced()) {
        media.style.removeProperty('--bf-par');
        return;
      }
      const rect = hero.getBoundingClientRect();
      if (rect.bottom < 0) return;
      const shift = Math.max(0, Math.min(-rect.top, rect.height)) * 0.18;
      media.style.setProperty('--bf-par', `${shift.toFixed(1)}px`);
    };
    window.addEventListener(
      'scroll',
      () => {
        if (!ticking) {
          ticking = true;
          window.requestAnimationFrame(update);
        }
      },
      { passive: true }
    );
    update();
  };

  /* ---------------- Mobile sticky CTA ---------------- */
  const initSticky = (root) => {
    const bar = root.querySelector('[data-bf-sticky]');
    const hero = root.querySelector('[data-bf-hero]');
    const signup = root.querySelector('#bf-prihlaseni');
    if (!bar || !hero || !('IntersectionObserver' in window)) return;
    bar.hidden = false;
    const state = { hero: true, signup: false };
    const apply = () => bar.classList.toggle('is-visible', !state.hero && !state.signup);
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.target === hero) state.hero = entry.isIntersecting;
          if (entry.target === signup) state.signup = entry.isIntersecting;
        });
        apply();
      },
      { threshold: 0 }
    );
    io.observe(hero);
    if (signup) io.observe(signup);
  };

  const init = () => {
    document.querySelectorAll('[data-bf]').forEach((root) => {
      if (root.dataset.bfReady) return;
      root.dataset.bfReady = '1';
      initTiles(root);
      initLadder(root);
      initTabs(root);
      initMultiAdd(root);
      initMeter(root);
      initCountdown(root);
      initVideo(root);
      initParallax(root);
      initSticky(root);
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();
